import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';

// Onboarding steps based on the UI flow
export enum OnboardingStep {
  WELCOME = 'welcome',
  OVERVIEW_CHART = 'overview_chart',
  SPENDING_DISTRIBUTION = 'spending_distribution',
  SPENDING_TRENDS = 'spending_trends',
  ASSET_OVERVIEW = 'asset_overview',
  CREDIT_CARD = 'credit_card',
  LOAN_BOOK = 'loan_book',
  ASSET_FLUCTUATION = 'asset_fluctuation',
  ADD_TRANSACTION = 'add_transaction',
  COMPLETED = 'completed',
}

// Step order for navigation
const STEP_ORDER: OnboardingStep[] = [
  OnboardingStep.WELCOME,
  OnboardingStep.OVERVIEW_CHART,
  OnboardingStep.SPENDING_DISTRIBUTION,
  OnboardingStep.SPENDING_TRENDS,
  OnboardingStep.ASSET_OVERVIEW,
  OnboardingStep.CREDIT_CARD,
  OnboardingStep.LOAN_BOOK,
  OnboardingStep.ASSET_FLUCTUATION,
  OnboardingStep.ADD_TRANSACTION,
  OnboardingStep.COMPLETED,
];

interface OnboardingContextType {
  // State
  isOnboardingActive: boolean;
  currentStep: OnboardingStep;
  isSampleDataLoaded: boolean;
  hasCompletedOnboarding: boolean;
  showCompletionModal: boolean;

  // Actions
  startOnboarding: () => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: OnboardingStep) => void;
  skipOnboarding: () => void;
  exitSampleData: () => void;
  completeOnboarding: () => void;
  resetOnboarding: () => void;
  triggerCompletionModal: () => void;
  hideCompletionModal: () => void;

  // Helpers
  getCurrentStepIndex: () => number;
  getTotalSteps: () => number;
  isFirstStep: () => boolean;
  isLastStep: () => boolean;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const ONBOARDING_STORAGE_KEY_PREFIX = 'mimo_onboarding_completed';

// Helper function to create user-specific storage key for onboarding completion
// Only persist completion status, NOT sample data loaded status (that's in-memory only)
const getOnboardingStorageKey = (userId: string | undefined) =>
  `${ONBOARDING_STORAGE_KEY_PREFIX}_${userId || 'anonymous'}`;

interface OnboardingProviderProps {
  children: React.ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const userId = user?.id;
  const prevUserIdRef = useRef<string | null>(null);
  const userIdRef = useRef<string | undefined>(userId);

  // Keep userIdRef in sync with userId
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // Initialize hasCompletedOnboarding from localStorage immediately to prevent race condition
  const getInitialCompletedState = () => {
    if (isAuthenticated && userId) {
      const onboardingKey = getOnboardingStorageKey(userId);
      return localStorage.getItem(onboardingKey) === 'true';
    }
    return false;
  };

  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(OnboardingStep.WELCOME);
  const [isSampleDataLoaded, setIsSampleDataLoaded] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(getInitialCompletedState);
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  // Check localStorage when user changes - only for completion status
  // Sample data loaded is in-memory only, so user starts fresh on refresh/close
  useEffect(() => {
    // Only check localStorage when user is authenticated and has a valid userId
    if (isAuthenticated && userId) {
      // Only run if userId actually changed
      if (prevUserIdRef.current !== userId) {
        const onboardingKey = getOnboardingStorageKey(userId);
        const completed = localStorage.getItem(onboardingKey);

        setHasCompletedOnboarding(completed === 'true');
        // Reset in-memory states - user starts fresh each session
        setIsSampleDataLoaded(false);
        setIsOnboardingActive(false);
        setCurrentStep(OnboardingStep.WELCOME);
        setShowCompletionModal(false);

        prevUserIdRef.current = userId;
      }
    } else {
      // User logged out - reset to default state
      if (prevUserIdRef.current !== null) {
        setHasCompletedOnboarding(false);
        setIsSampleDataLoaded(false);
        setIsOnboardingActive(false);
        setCurrentStep(OnboardingStep.WELCOME);
        setShowCompletionModal(false);
        prevUserIdRef.current = null;
      }
    }
  }, [userId, isAuthenticated]);

  const startOnboarding = useCallback(() => {
    setIsOnboardingActive(true);
    setCurrentStep(OnboardingStep.WELCOME);
    setIsSampleDataLoaded(true);
    // Note: sample data loaded is in-memory only, not persisted
    // If user closes/refreshes, they start fresh with the welcome modal
  }, []);

  const nextStep = useCallback(() => {
    const currentIndex = STEP_ORDER.indexOf(currentStep);
    if (currentIndex < STEP_ORDER.length - 1) {
      setCurrentStep(STEP_ORDER[currentIndex + 1]);
    }
  }, [currentStep]);

  const previousStep = useCallback(() => {
    const currentIndex = STEP_ORDER.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEP_ORDER[currentIndex - 1]);
    }
  }, [currentStep]);

  const goToStep = useCallback((step: OnboardingStep) => {
    setCurrentStep(step);
  }, []);

  const skipOnboarding = useCallback(() => {
    setIsOnboardingActive(false);
    setIsSampleDataLoaded(false);
    setCurrentStep(OnboardingStep.COMPLETED);
    setHasCompletedOnboarding(true);
    localStorage.setItem(getOnboardingStorageKey(userIdRef.current), 'true');
  }, []);

  const exitSampleData = useCallback(() => {
    setIsOnboardingActive(false);
    setIsSampleDataLoaded(false);
    setCurrentStep(OnboardingStep.COMPLETED);
    setHasCompletedOnboarding(true);
    localStorage.setItem(getOnboardingStorageKey(userIdRef.current), 'true');
  }, []);

  const completeOnboarding = useCallback(() => {
    setIsOnboardingActive(false);
    setIsSampleDataLoaded(false);
    setCurrentStep(OnboardingStep.COMPLETED);
    setHasCompletedOnboarding(true);
    localStorage.setItem(getOnboardingStorageKey(userIdRef.current), 'true');
  }, []);

  const resetOnboarding = useCallback(() => {
    setIsOnboardingActive(false);
    setCurrentStep(OnboardingStep.WELCOME);
    setIsSampleDataLoaded(false);
    setHasCompletedOnboarding(false);
    localStorage.removeItem(getOnboardingStorageKey(userIdRef.current));
  }, []);

  const triggerCompletionModal = useCallback(() => {
    setIsOnboardingActive(false);
    setShowCompletionModal(true);
  }, []);

  const hideCompletionModal = useCallback(() => {
    setShowCompletionModal(false);
  }, []);

  const getCurrentStepIndex = useCallback(() => {
    return STEP_ORDER.indexOf(currentStep);
  }, [currentStep]);

  const getTotalSteps = useCallback(() => {
    // Exclude WELCOME and COMPLETED from count
    return STEP_ORDER.length - 2;
  }, []);

  const isFirstStep = useCallback(() => {
    return currentStep === OnboardingStep.WELCOME || currentStep === OnboardingStep.OVERVIEW_CHART;
  }, [currentStep]);

  const isLastStep = useCallback(() => {
    return currentStep === OnboardingStep.ADD_TRANSACTION;
  }, [currentStep]);

  const value: OnboardingContextType = {
    isOnboardingActive,
    currentStep,
    isSampleDataLoaded,
    hasCompletedOnboarding,
    showCompletionModal,
    startOnboarding,
    nextStep,
    previousStep,
    goToStep,
    skipOnboarding,
    exitSampleData,
    completeOnboarding,
    resetOnboarding,
    triggerCompletionModal,
    hideCompletionModal,
    getCurrentStepIndex,
    getTotalSteps,
    isFirstStep,
    isLastStep,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export default OnboardingContext;
